// Pacts: offered to a rival as the political action (no military actions), who accepts or declines while the
//   offerer's turn waits. For a one-sided pact the offerer picks who is A and who is B. Declined, the card goes back to
//   the offerer's hand (everyone has seen it). Accepted, it stays in play between the two, changing both civilizations
//   until it is cancelled (a political action of either side), ended by an aggression between them (some pacts), or
//   made obsolete (two ages old, like leaders); then it is discarded.
//   A player has at most one pact in play that they offered: offering another is allowed, and if it is accepted the
//   old one is discarded. A player can be in any number of pacts offered by others.
//   "Attack" means an aggression or declaring a war: some pacts forbid them between the two, some end with one; none
//   stops wars already declared.
// Pacts in play are kept in the game state: [{ card, offerer, a, b }] (a: civilization A, b: civilization B; for an
// even-handed pact a is the offerer). The offer waiting for an answer: pactOffer, { card, offerer, partner, a, b }.
const CardConverter = require('../Utilities/Converters/CardConverter');
const CardToData = require('../Utilities/Converters/CardToData');
const PactNames = require('../Constants/Names/PactNames');
const { AgeOrder } = require('../Constants/Constants');
const { Add, AddCostModifier, CostKinds, AddResourcePool, PoolKinds } = require('../Effects/Effects');
const { RequestDecision } = require('./Decisions');
const { GetAllStats, PlayerChanges } = require('../Utilities/Stats');
const { CardRef } = require('../Utilities/CardRefs');

// Extra (or less) food or resources produced at the end of each turn, after the cards' own production
// (a production bonus: produced with the others, see PlayerState.ProduceBonus)
const produce = (player, card, currency, amount) => {
    Add(player, card, currency, amount);
};

// What each pact does. a / b: what civilization A and B get, as text (both: an even-handed pact, the same for each);
// effects(a, b, card): adds it to both players. noAttack: no attacks (aggressions or wars) between the two; endsOnAttack: an attack
// between them ends it; attackBonus: strength added to an attack on the other; noWarOnB: no one may declare
// war on B.
const Pacts = {
    [PactNames.AcceptanceOfSupremacy]: {
        a: "+1 resource/turn", b: "-1 resource/turn", noAttack: true,
        effects: (a, b, card) => { produce(a, card, 'resources', 1); produce(b, card, 'resources', -1); },
    },
    [PactNames.InternationalTourism]: {
        both: "+1 culture/turn per wonder the other has",
        effects: (a, b, card) => {
            Add(a, card, 'culture', () => b.wonders.length);
            Add(b, card, 'culture', () => a.wonders.length);
        },
    },
    [PactNames.InternationalTradeAgreement]: {
        a: "+1 resource/turn", b: "+1 food/turn",
        effects: (a, b, card) => { produce(a, card, 'resources', 1); produce(b, card, 'food', 1); },
    },
    [PactNames.LossOfSovereignty]: {
        a: "+2 culture/turn", b: "-2 culture/turn; no wars can be declared on them",
        noAttack: true, noWarOnB: true,
        effects: (a, b, card) => { Add(a, card, 'culture', 2); Add(b, card, 'culture', -2); },
    },
    [PactNames.MilitaryAlliance]: {
        both: "+3 strength", endsOnAttack: true,
        effects: (a, b, card) => { Add(a, card, 'strength', 3); Add(b, card, 'strength', 3); },
    },
    [PactNames.NavalTradeAgreement]: {
        a: "+1 colonization, -1 blue token", b: "+2 blue tokens",
        effects: (a, b, card) => {
            Add(a, card, 'colonization', 1);
            Add(a, card, 'blueTokens', -1);
            Add(b, card, 'blueTokens', 2);
        },
    },
    [PactNames.OpenBordersAgreement]: {
        both: "+1 military action; +2 strength when attacking the other", attackBonus: 2,
        effects: (a, b, card) => { Add(a, card, 'militaryActions', 1); Add(b, card, 'militaryActions', 1); },
    },
    [PactNames.PeaceTreaty]: {
        both: "+1 culture/turn", noAttack: true,
        effects: (a, b, card) => { Add(a, card, 'culture', 1); Add(b, card, 'culture', 1); },
    },
    [PactNames.PromiseOfMilitaryProtection]: {
        a: "+1 culture/turn", b: "+4 strength, -1 culture/turn", endsOnAttack: true,
        effects: (a, b, card) => {
            Add(a, card, 'culture', 1);
            Add(b, card, 'strength', 4);
            Add(b, card, 'culture', -1);
        },
    },
    [PactNames.ScientificCooperation]: {
        both: "-2 science to discover a technology (the other gets 1 science)",
        // The partner's 1 science: see CooperationPartner
        effects: (a, b, card) => {
            AddCostModifier(a, card, CostKinds.DevelopScience, -2);
            AddCostModifier(b, card, CostKinds.DevelopScience, -2);
        },
    },
    [PactNames.TradeRoutesAgreement]: {
        a: "once a turn, 1 food for 1 resource", b: "once a turn, 1 resource for 1 food",
        effects: (a, b, card) => {
            AddResourcePool(a, card, PoolKinds.Resources, 1, 'food');
            AddResourcePool(b, card, PoolKinds.Food, 1, 'resources');
        },
    },
};

const IsOneSided = (name) => Pacts[name]?.both === undefined;

// What each side would get, e.g. { 0: "produces 1 extra resource each turn", 2: "..." }
const Terms = (name, a, b) => {
    const pact = Pacts[name];
    return { [a]: pact.both ?? pact.a, [b]: pact.both ?? pact.b };
};

// ---------------- Pacts in play

const Between = (gameState, one, other) => gameState.pacts.filter(pact =>
    (pact.a === one && pact.b === other) || (pact.a === other && pact.b === one));
const PactsOf = (gameState, player) => gameState.pacts.filter(pact => pact.a === player || pact.b === player);
const OfferedBy = (gameState, player) => gameState.pacts.find(pact => pact.offerer === player) ?? null;
const OtherSide = (pact, player) => pact.a === player ? pact.b : pact.a;

// Built into the players when the game state is read (the card is kept with the pact while it is, not saved)
const ApplyPactEffects = (gameState) => gameState.pacts.forEach(pact => {
    const card = CardConverter(pact.card);
    Object.defineProperty(pact, 'source', { value: card, enumerable: false, writable: true });
    Pacts[pact.card.name]?.effects(gameState.playerStates[pact.a], gameState.playerStates[pact.b], card);
});

// Takes a pact's effects off both players at once (rather than when the game state is next read), so that what
// follows in the same move sees them gone (e.g. strengths compared in an aggression that ended it)
const removeEffects = (gameState, pact) => [pact.a, pact.b].forEach(number => {
    const player = gameState.playerStates[number];
    Object.keys(player).filter(key => key.endsWith('Delegate') && Array.isArray(player[key])).forEach(key => {
        player[key] = player[key].filter(delegate => delegate.Source !== pact.source);
    });
});

// A pact ending, as a line under what ended it: "[pact] between Andrew and Matt cancelled"
const EndedLine = (gameState, pact, cancelled = 'cancelled') =>
    `${CardRef(pact.card)} between ${gameState.playerStates[pact.offerer].name} and ${gameState.playerStates[OtherSide(pact, pact.offerer)].name} ${cancelled}`;

// A recap line about a pact (step: offer, accept, decline or end), told from the player acting (the offerer, unless
// it's the partner's answer): "Andrew offers [pact] to Dave", "Dave accepts [pact]", "[pact] between Andrew and Matt
// cancelled by Andrew"
const report = (gameState, pact, step, text, by = pact.offerer) => gameState.reports.push({
    name: pact.card.name, age: pact.card.age, card: pact.card, kind: 'pact', pactStep: step, round: gameState.round,
    revealedBy: gameState.playerStates[by].name,
    target: gameState.playerStates[by === pact.offerer ? OtherSide(pact, pact.offerer) : pact.offerer].name,
    text, changes: [],
});

// Takes a pact out of play (without its recap line)
const remove = (gameState, pact) => {
    // Its tokens go with it (Naval Trade Agreement): a shortfall is settled as the pact's ending
    gameState.blueCause = 'Pact cancellation resolution';
    removeEffects(gameState, pact);
    gameState.pacts = gameState.pacts.filter(p => p !== pact);
    gameState.militaryDiscard.push(CardConverter(pact.card));
};

// Discards a pact in play, for a reason (as log text: "cancelled by Andrew")
const discard = (gameState, pact, text) => {
    remove(gameState, pact);
    report(gameState, pact, 'end', text);
};

// ---------------- Offering, answering, cancelling

// For the offer picker: each rival, the roles that can be chosen and what each side would get, and what offering
// would end (the offerer's pact in play, if accepted)
const OfferTargets = (gameState, offerer, card) => {
    const current = OfferedBy(gameState, offerer.playerNumber);
    return gameState.playerStates.filter(p => p !== offerer && !p.resigned).map(partner => ({
        player: partner.playerNumber,
        name: partner.name,
        roles: (IsOneSided(card.name) ? [true, false] : [true]).map(offererIsA => {
            const [a, b] = offererIsA ? [offerer.playerNumber, partner.playerNumber] : [partner.playerNumber, offerer.playerNumber];
            const terms = Terms(card.name, a, b);
            return { offererIsA, you: terms[offerer.playerNumber], them: terms[partner.playerNumber] };
        }),
        warning: current ? `If accepted, your ${current.card.name} with ${gameState.playerStates[OtherSide(current, offerer.playerNumber)].name} ends` : null,
        // (the same for every rival, as it comes from offering at all: the pact that would end, and with whom)
        ends: current ? { card: current.card, with: OtherSide(current, offerer.playerNumber) } : null,
    }));
};

const OfferProblem = (gameState, offerer, partner) => {
    if (!partner || partner === offerer || partner.resigned)
    {
        return "Choose a rival";
    }
    return null;
};

const OfferPact = (gameState, offerer, partner, card, offererIsA) => {
    const [a, b] = offererIsA || !IsOneSided(card.name)
        ? [offerer.playerNumber, partner.playerNumber] : [partner.playerNumber, offerer.playerNumber];
    gameState.pactOffer = { card: CardToData(card), offerer: offerer.playerNumber, partner: partner.playerNumber, a, b };
    const terms = Terms(card.name, a, b);
    const current = OfferedBy(gameState, offerer.playerNumber);
    const sides = IsOneSided(card.name)
        ? `${offerer.name}: ${terms[offerer.playerNumber]}; you: ${terms[partner.playerNumber]}`
        : `each of you ${terms[a]}`;
    RequestDecision(gameState, {
        player: partner, type: 'pactOffer', event: card.name, blocking: true,
        prompt: `${offerer.name} offers you ${CardRef(card)}: ${sides}.${current ? ` (It would end ${offerer.name}'s ${CardRef(current.card)}.)` : ''}`,
        options: [
            { label: "Accept", value: 'accept', text: `Accept ${card.name}` },
            { label: "Decline", value: 'decline', text: `Decline: the card goes back to ${offerer.name}'s hand` },
        ],
        context: { terms, a, b },
    });
    // Everyone's "since your last turn" shows the offer while it waits, a line for what each side would get and one for
    // the pact it would end (its answer, reported when made, confirms them or not)
    gameState.reports.push({
        name: card.name, age: card.age, card: CardToData(card), kind: 'pact', pactStep: 'offer', round: gameState.round,
        revealedBy: offerer.name, target: partner.name, text: '', changes: [], revealCards: [card.id],
        lines: [`${offerer.name}: ${terms[offerer.playerNumber]}`, `${partner.name}: ${terms[partner.playerNumber]}`,
            ...(current ? [EndedLine(gameState, current, 'would be cancelled')] : [])],
    });
};

// The partner's answer
const AnswerOffer = (gameState, accept) => {
    const offer = gameState.pactOffer;
    const offerer = gameState.playerStates[offer.offerer];
    const partner = gameState.playerStates[offer.partner];
    gameState.pactOffer = null;
    const pact = { card: offer.card, offerer: offer.offerer, a: offer.a, b: offer.b };
    if (!accept)
    {
        offerer.militaryHand.push(CardConverter(offer.card));
        gameState.reports.push({
            name: offer.card.name, age: offer.card.age, card: offer.card, kind: 'pact', pactStep: 'decline', round: gameState.round,
            revealedBy: partner.name, target: offerer.name, text: '', changes: [],
            revealCards: [offer.card.id],
        });
        return `${partner.name} declines ${CardRef(offer.card)}`;
    }
    const current = OfferedBy(gameState, offer.offerer);
    if (current)
    {
        remove(gameState, current);
    }
    gameState.pacts.push(pact);
    // In effect at once (for the rest of this move too)
    Object.defineProperty(pact, 'source', { value: CardConverter(pact.card), enumerable: false, writable: true });
    Pacts[pact.card.name]?.effects(gameState.playerStates[pact.a], gameState.playerStates[pact.b], pact.source);
    // (what it does, and the pact it replaces, are listed under the offer: accepting confirms them)
    report(gameState, pact, 'accept', '', partner.playerNumber);
    return `${partner.name} accepts ${CardRef(offer.card)}`;
};

const CancelPact = (gameState, player, pact) => discard(gameState, pact, `cancelled by ${player.name}`);

// ---------------- Aggressions and wars

// Why the aggressor may not attack the defender because of a pact, or null
const AttackProblem = (gameState, aggressor, defender) => {
    const pact = Between(gameState, aggressor.playerNumber, defender.playerNumber).find(p => Pacts[p.card.name]?.noAttack);
    return pact ? `Your ${pact.card.name} with ${defender.name} forbids attacks between you` : null;
};

// Strength added to an aggression against this rival (Open Borders Agreement)
const AttackBonus = (gameState, aggressor, defender) => Between(gameState, aggressor.playerNumber, defender.playerNumber)
    .reduce((sum, pact) => sum + (Pacts[pact.card.name]?.attackBonus ?? 0), 0);

// The pacts an aggression between the two would end
const EndedByAttack = (gameState, aggressor, defender) => Between(gameState, aggressor.playerNumber, defender.playerNumber)
    .filter(pact => Pacts[pact.card.name]?.endsOnAttack);

// An aggression is played or a war declared: those pacts end before it is resolved. Returns a line for each, for the
// aggression's or war's recap (where it is listed under what ended it)
const EndPactsOnAttack = (gameState, aggressor, defender) => EndedByAttack(gameState, aggressor, defender).map(pact => {
    remove(gameState, pact);
    return EndedLine(gameState, pact);
});

// Why no one may declare war on this rival, or null (Loss of Sovereignty)
const WarDeclarationProblem = (gameState, defender) => {
    const pact = gameState.pacts.find(p => p.b === defender.playerNumber && Pacts[p.card.name]?.noWarOnB);
    return pact ? `No one can declare war on ${defender.name} (${pact.card.name})` : null;
};

// ---------------- Other rules

// Scientific Cooperation: the pact-mate who pays 1 science when this player discovers a technology, or null
// (With Development of Civilization, pact-mates choose one after the other, the second from options worked out after
// the first's discovery: see Decisions RefreshNext)
const CooperationPartner = (gameState, player) => {
    const pact = PactsOf(gameState, player.playerNumber).find(p => p.card.name === PactNames.ScientificCooperation);
    return pact ? gameState.playerStates[OtherSide(pact, player.playerNumber)] : null;
};
const CooperationProblem = (gameState, player) => {
    const partner = CooperationPartner(gameState, player);
    return partner && partner.science < 1 ? `${partner.name} has no science to pay for Scientific Cooperation` : null;
};
const PayCooperation = (gameState, player) => {
    const partner = CooperationPartner(gameState, player);
    if (partner)
    {
        partner.science -= 1;
    }
};

// A new age: pacts two ages old are discarded (as leaders are); returns what was discarded, as text
const RetirePacts = (gameState, obsoleteAge) => {
    const old = gameState.pacts.filter(pact => pact.card.age === obsoleteAge);
    old.forEach(pact => discard(gameState, pact, `cancelled: ${obsoleteAge} cards are obsolete`));
    return old.map(pact => pact.card.name);
};

// Trade Routes Agreement: the pool the player can choose to use this turn, if any ({ source, payWith, left })
const TradeRoute = (player) => {
    const pool = player.resourcePoolDelegate.find(p => p.payWith);
    if (!pool)
    {
        return null;
    }
    const source = pool.Source?.name;
    return { source, payWith: pool.payWith, instead: pool.kind, left: Math.max(0, pool.Action() - (player.turnPoolUsage[source] ?? 0)) };
};

module.exports = {
    PactEffects: Pacts,
    IsOneSided,
    Terms,
    Between,
    PactsOf,
    OfferedBy,
    OtherSide,
    ApplyPactEffects,
    OfferTargets,
    OfferProblem,
    OfferPact,
    AnswerOffer,
    CancelPact,
    AttackProblem,
    AttackBonus,
    EndedByAttack,
    EndPactsOnAttack,
    WarDeclarationProblem,
    CooperationPartner,
    CooperationProblem,
    PayCooperation,
    RetirePacts,
    TradeRoute,
    AgeOrder,
};
